import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { COVID_MESSAGE_DIALOG, COVID_MESSAGE_DIALOG_ID } from "@obg/mat.ui.common";
import { DialogComponentData, DialogLayoutComponent, DialogService, NavbarOptions } from "@obg/mat.ui.core";
import { CommonState, VerificationType } from "@obg/ngx.common";
import {
	CORE_ROUTE,
	CovidMeasuresConfigService,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { Subscription } from "rxjs";
import { filter, tap, withLatestFrom } from "rxjs/operators";

import { CustomerVerificationFlowConfigService } from "./customer-verification-flow.config";
import { DialogRouteConfig, RegisterSuccessQueryParams } from "./customer-verification.model";

export const CUSTOMER_VERIFICATION_DIALOG_ID = "customer-verification";

@Component({
	selector: "obg-m-customer-verification-dialog-container",
	styleUrls: ["./customer-verification-dialog.container.scss"],
	templateUrl: "./customer-verification-dialog.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class CustomerVerificationDialogContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-customer-verification-dialog") hostClass = true;
	@ViewChild("dialog") dialogLayout!: DialogLayoutComponent;

	readonly showHeaderAndFooter: boolean;

	name: string | undefined;
	username: string | undefined;
	welcomeMessageKey: string | undefined;
	variant: VerificationType | undefined;
	navbarOptions: NavbarOptions = {
		hideBackButton: true,
		showCloseButton: true,
		preventDefaultBack: true,
	};

	private readonly isDesktop: boolean;

	private nextRoute: DialogRouteConfig | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState>,
		private dialogService: DialogService,
		private covidMeasuresConfig: CovidMeasuresConfigService,
		deviceConfigService: DeviceConfigService,
		private customerVerificationFlowConfigService: CustomerVerificationFlowConfigService,
		private changeDetector: ChangeDetectorRef
	) {
		this.isDesktop = deviceConfigService.isDesktop();
		this.showHeaderAndFooter =
			!this.isDesktop && this.customerVerificationFlowConfigService.showHeaderAndFooterForNDCFlow();
	}

	ngOnInit() {
		const params$ = this.store.select<RegisterSuccessQueryParams | undefined>(RouteSelector.getQueryParams).pipe(
			ignoreNil(),
			filter((params) => !!params.variant),
			withLatestFrom(
				this.store.select(CustomerManagementSelector.getCustomer),
				this.store.select(JurisdictionSelector.get)
			),
			tap(([params, customer, jurisdiction]) => {
				if (this.covidMeasuresConfig.covidMeasuresEnabled() && jurisdiction === Jurisdiction.Ukgc) {
					this.showCovidMessage();
				}
				this.name = customer?.profile?.firstName;
				this.variant = params.variant;
				this.username = params.username;

				if (jurisdiction === Jurisdiction.Ksa && this.variant === VerificationType.verified) {
					this.welcomeMessageKey = "customer-verification.email-verified";
				} else {
					this.welcomeMessageKey = this.name
						? "customer-verification.customer-welcome"
						: "customer-verification.welcome";
				}
				this.store.dispatch(RouteAction.updateQueryParams({ name: null, username: null, variant: null }));
				this.changeDetector.detectChanges();
			})
		);

		this.data$$ = params$.subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();

		if (!this.nextRoute) {
			return;
		}

		const nextRouteData = this.nextRoute.config && this.nextRoute.config.data;
		const skipRewardClaim = nextRouteData && !!nextRouteData.skipRewardClaim;
		if (!this.isDesktop && !skipRewardClaim && this.variant === VerificationType.withRewards) {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.reward.welcomeBonus));
			return;
		}

		this.store.dispatch(
			RouteAction.navigate(this.nextRoute.routeName, undefined, {
				queryParams: {
					...nextRouteData,
					username: this.nextRoute.routeName === CORE_ROUTE.auth.login ? this.username : undefined,
					redirect:
						!this.isDesktop && this.variant === VerificationType.created ? CORE_ROUTE.home : undefined,
					skipWelcomeBonus: !this.isDesktop && skipRewardClaim ? true : undefined,
				},
			})
		);
	}

	closeDialog(nextRoute?: DialogRouteConfig) {
		this.nextRoute = nextRoute;
		this.dialogLayout.close();
	}

	private showCovidMessage() {
		const data: DialogComponentData = {
			hideClose: false,
			hideFooter: false,
			title: "responsible-gaming.title",
		};

		this.dialogService.openComponent(COVID_MESSAGE_DIALOG, {
			id: COVID_MESSAGE_DIALOG_ID,
			disableClose: false,
			data,
		});
	}
}
